/**
 * B1019 — gift cards, the back-office doors (B1018 leg (a), the api lane).
 * CLASS A, RULED 2026-10-30 by Pathik ("go on B1019"): the defaults on the
 * B1018 line stand. The model and the money live in packages/domain
 * (gift-card.ts, gift-card-store.ts). The public balance door and the
 * checkout tender join in the next slices.
 *
 * Custody: a card's number and PIN cross the wire ONCE — in giftcard.issue's
 * answer — and are stored only as digests; every other answer names the card
 * by its id and last four.
 */
import { z } from "zod";
import { publicOp, readOp, writeOp, zEpochMs, zId, zMoney, zNonNegMoney } from "./common.js";

const zStatus = z.enum(["scheduled", "active", "used_up", "frozen", "voided"]);
const zReason = z.string().min(1).max(200).regex(/\S/);
const zMinor = z.number().int().min(1).max(100_000_000);

export const zGiftCardSettings = z
  .object({
    enabled: z.boolean(),
    /** 1–6 digits, never leading 3/4/5/6 (bank card ranges). */
    prefix: z.string().regex(/^(?![3456])\d{1,6}$/),
    presets: z.array(zMinor).max(8),
    customMin: zMinor,
    customMax: zMinor,
    caps: z
      .object({ cardsPerOrder: z.number().int().min(1).max(50), perOrder: zMinor, perBuyerPerDay: zMinor })
      .strict(),
  })
  .strict();

const zIssuedBy = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("order"), orderId: zId }).strict(),
  z.object({ kind: z.literal("till"), saleId: zId, staffId: zId }).strict(),
  z.object({ kind: z.literal("staff"), staffId: zId, reason: zReason }).strict(),
]);

const zParty = z.object({ name: z.string().max(120).optional(), email: z.string().email().max(254).optional() }).strict();

export const zGiftCardView = z
  .object({
    giftCardId: zId,
    lastFour: z.string().regex(/^\d{4}$/),
    status: zStatus,
    loaded: zMoney,
    balance: zNonNegMoney,
    issuedAt: zEpochMs,
    issuedBy: zIssuedBy,
    sendAt: zEpochMs.optional(),
    recipient: zParty.optional(),
    sender: zParty.optional(),
    message: z.string().max(200).optional(),
    designId: zId.optional(),
    /** B1019 (6) — the card this one replaced / the card that replaced it (ids, never numbers). */
    reissuedFrom: zId.optional(),
    reissuedTo: zId.optional(),
  })
  .strict();

const EXAMPLE_CARD = {
  giftCardId: "gc_7f3a",
  lastFour: "8903",
  status: "active",
  loaded: { amount: 5000, currency: "USD" },
  balance: { amount: 5000, currency: "USD" },
  issuedAt: 1_780_000_000_000,
  issuedBy: { kind: "staff", staffId: "b1c2d3e4-staff", reason: "Rained out" },
} as const;

const EXAMPLE_SETTINGS = {
  enabled: true,
  prefix: "7342",
  presets: [2500, 5000, 10000],
  customMin: 1000,
  customMax: 50000,
  caps: { cardsPerOrder: 10, perOrder: 200000, perBuyerPerDay: 200000 },
} as const;

export const giftcardSettingsGet = readOp({
  name: "giftcard.settings.get",
  permission: "ledger:read",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: {},
  response: zGiftCardSettings,
  exampleRequest: {},
  exampleResponse: EXAMPLE_SETTINGS,
});

export const giftcardSettingsSet = writeOp({
  name: "giftcard.settings.set",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { settings: zGiftCardSettings },
  response: zGiftCardSettings,
  exampleRequest: { settings: EXAMPLE_SETTINGS },
  exampleResponse: EXAMPLE_SETTINGS,
});

/**
 * B1020 — the storefront's read of the offer (842:2's facts): on or off, the
 * home currency, the preset amounts, the custom range and how many cards one
 * order may hold. The buyer's own session reads it (and the till, and staff);
 * the number prefix and the money caps stay behind the back-office doors.
 * `designs` is the tenant's uploaded card faces (B1024 fills it; empty until
 * then, and the page draws the house face).
 */
export const giftcardOffer = readOp({
  name: "giftcard.offer",
  permission: "catalog:read",
  principals: ["CUSTOMER", "STAFF", "DEVICE:KIOSK"],
  fields: {},
  response: z
    .object({
      enabled: z.boolean(),
      currency: z.string().regex(/^[A-Z]{3}$/),
      presets: z.array(zMinor).max(8),
      customMin: zMinor,
      customMax: zMinor,
      cardsPerOrder: z.number().int().min(1).max(50),
      designs: z.array(z.object({ designId: zId, name: z.string().min(1).max(60) }).strict()).max(24),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: { enabled: true, currency: "USD", presets: [2500, 5000, 10000], customMin: 1000, customMax: 50000, cardsPerOrder: 10, designs: [] },
});

/** Staff goodwill / comps (contra revenue). Online and till sales issue through their own flows. */
export const giftcardIssue = writeOp({
  name: "giftcard.issue",
  permission: "refund:write",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: {
    amount: zMinor,
    reason: zReason,
    recipient: zParty.optional(),
    message: z.string().max(200).optional(),
    sendAt: zEpochMs.optional(),
    designId: zId.optional(),
  },
  response: z
    .object({
      card: zGiftCardView,
      /** Shown ONCE — stored only as digests. */
      number: z.string().regex(/^\d{16}$/),
      pin: z.string().regex(/^\d{4}$/),
    })
    .strict(),
  exampleRequest: { amount: 5000, reason: "Rained out" },
  exampleResponse: { card: EXAMPLE_CARD, number: "7342012345678903", pin: "0479" },
});

export const giftcardGet = readOp({
  name: "giftcard.get",
  permission: "ledger:read",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: { giftCardId: zId },
  response: zGiftCardView,
  exampleRequest: { giftCardId: "gc_7f3a" },
  exampleResponse: EXAMPLE_CARD,
});

export const giftcardList = readOp({
  name: "giftcard.list",
  permission: "ledger:read",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: { status: zStatus.optional() },
  response: z.object({ cards: z.array(zGiftCardView) }).strict(),
  exampleRequest: {},
  exampleResponse: { cards: [EXAMPLE_CARD] },
});

export const giftcardFreeze = writeOp({
  name: "giftcard.freeze",
  permission: "refund:write",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: { giftCardId: zId, reason: zReason },
  response: zGiftCardView,
  exampleRequest: { giftCardId: "gc_7f3a", reason: "Reported stolen" },
  exampleResponse: { ...EXAMPLE_CARD, status: "frozen" },
});

export const giftcardUnfreeze = writeOp({
  name: "giftcard.unfreeze",
  permission: "refund:write",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: { giftCardId: zId },
  response: zGiftCardView,
  exampleRequest: { giftCardId: "gc_7f3a" },
  exampleResponse: EXAMPLE_CARD,
});

/** MANAGER (or ADMIN), with a reason — journalled against contra revenue. */
export const giftcardAdjust = writeOp({
  name: "giftcard.adjust",
  permission: "refund:write",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: { giftCardId: zId, delta: z.number().int().min(-100_000_000).max(100_000_000), reason: zReason },
  response: zGiftCardView,
  exampleRequest: { giftCardId: "gc_7f3a", delta: 300, reason: "Lost the first card at the gate" },
  exampleResponse: { ...EXAMPLE_CARD, balance: { amount: 5300, currency: "USD" } },
});

/**
 * B1019 (6) — a lost card: the old number is voided and a NEW number + PIN
 * (shown once) carries its balance and words. No journal moves — the same
 * liability, a new number. Asks why (the audit row keeps it).
 */
export const giftcardReissue = writeOp({
  name: "giftcard.reissue",
  permission: "refund:write",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: { giftCardId: zId, reason: zReason },
  response: z
    .object({
      card: zGiftCardView,
      /** Shown ONCE — stored only as digests. */
      number: z.string().regex(/^\d{16}$/),
      pin: z.string().regex(/^\d{4}$/),
      replaced: zGiftCardView,
    })
    .strict(),
  exampleRequest: { giftCardId: "gc_7f3a", reason: "Guest lost the card at the gate" },
  exampleResponse: {
    card: { ...EXAMPLE_CARD, giftCardId: "gc_7f3a-r", lastFour: "4410", reissuedFrom: "gc_7f3a" },
    number: "7342019876544410",
    pin: "2281",
    replaced: { ...EXAMPLE_CARD, status: "voided", reissuedTo: "gc_7f3a-r" },
  },
});

const zStatusCounts = z.object(Object.fromEntries(zStatus.options.map((s) => [s, z.number().int().min(0)])) as Record<z.infer<typeof zStatus>, z.ZodNumber>).strict();

/** B1019 (6) — the book: per currency, cards issued, what they were loaded with, and what is still owed (the liability). */
export const giftcardReport = readOp({
  name: "giftcard.report",
  permission: "ledger:read",
  principals: ["STAFF:ADMIN", "STAFF:MANAGER"],
  fields: {},
  response: z
    .object({
      currencies: z.array(z.object({ currency: z.string().regex(/^[A-Z]{3}$/), issued: z.number().int().min(0), loaded: zNonNegMoney, outstanding: zNonNegMoney, byStatus: zStatusCounts }).strict()),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    currencies: [
      { currency: "USD", issued: 2, loaded: { amount: 7500, currency: "USD" }, outstanding: { amount: 5000, currency: "USD" }, byStatus: { scheduled: 0, active: 1, used_up: 1, frozen: 0, voided: 0 } },
    ],
  },
});

/**
 * The public balance check (no sign-in): number + PIN. The answer is the
 * balance, the last four and the status — never the number, the history's
 * buyer, or anything another door could enumerate. Walls in
 * docs/ABUSE-CONTROLS.md.
 */
export const giftcardBalance = publicOp({
  name: "giftcard.balance",
  permission: "public",
  fields: {
    /** 16 digits as people type them — spaces and hyphens allowed. */
    number: z.string().min(16).max(24).regex(/^[\d\s-]+$/),
    pin: z.string().regex(/^\d{4}$/),
  },
  response: z
    .object({
      lastFour: z.string().regex(/^\d{4}$/),
      status: zStatus,
      balance: zNonNegMoney,
      /** B1022 — what the card was loaded with ("$32.00 of $50.00 loaded"). */
      loaded: zNonNegMoney,
      /** B1022 — the card's life from the ledger; no order, reason or name ever rides it. */
      history: z.array(z.object({ kind: z.enum(["loaded", "used", "refunded", "adjusted"]), at: z.number().int().nonnegative(), amount: z.object({ amount: z.number().int(), currency: z.string() }).strict() }).strict()).max(200),
    })
    .strict(),
  exampleRequest: { number: "7342 0123 4567 8903", pin: "0479" },
  exampleResponse: {
    lastFour: "8903",
    status: "active",
    balance: { amount: 3200, currency: "USD" },
    loaded: { amount: 5000, currency: "USD" },
    history: [
      { kind: "loaded", at: 1_790_000_000_000, amount: { amount: 5000, currency: "USD" } },
      { kind: "used", at: 1_790_100_000_000, amount: { amount: 1800, currency: "USD" } },
    ],
  },
});

/**
 * B1019 (5a) — spend a gift card on a pending order: number + PIN. The card
 * pays what it can of what the order still needs, held until the order
 * confirms (drawn) or does not (released); the card that covers the rest
 * confirms the order. The buyer's own payment door — the same principals as
 * payment.intent.create but an API key (a card's PIN is the guest's secret).
 */
export const giftcardRedeem = writeOp({
  name: "giftcard.redeem",
  owner: "customer",
  permission: "payment:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: {
    orderId: zId,
    /** 16 digits as people type them — spaces and hyphens allowed. */
    number: z.string().min(16).max(24).regex(/^[\d\s-]+$/),
    pin: z.string().regex(/^\d{4}$/),
  },
  response: z
    .object({
      intentId: zId,
      lastFour: z.string().regex(/^\d{4}$/),
      /** What this card pays on this order. */
      amount: zMoney,
      /** What stays on the card once this order confirms — the receipt's line. */
      left: zNonNegMoney,
      /** What the order still needs by bank card (zero = paid). */
      leftToPay: zNonNegMoney,
      orderState: z.enum(["PENDING_PAYMENT", "CONFIRMED"]),
    })
    .strict(),
  exampleRequest: { orderId: "order-c-1", number: "7342 0123 4567 8903", pin: "0479" },
  exampleResponse: {
    intentId: "pi-order-c-1-1",
    lastFour: "8903",
    amount: { amount: 5000, currency: "USD" },
    left: { amount: 0, currency: "USD" },
    leftToPay: { amount: 1000, currency: "USD" },
    orderState: "PENDING_PAYMENT",
  },
});

/**
 * B1021 — REMOVE on the Pay step: take an applied card back off the buyer's
 * own pending order. Its hold goes back to the card and its `gift_card`
 * intent is cancelled; the answer is what the order now needs by bank card.
 * A card on an order that confirmed is drawn — it does not come off.
 */
export const giftcardUnapply = writeOp({
  name: "giftcard.unapply",
  owner: "customer",
  permission: "payment:write",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: {
    orderId: zId,
    /** The intent giftcard.redeem answered for this card. */
    intentId: zId,
  },
  response: z
    .object({
      intentId: zId,
      /** What the order still needs by bank card once the card is off. */
      leftToPay: zNonNegMoney,
    })
    .strict(),
  exampleRequest: { orderId: "order-c-1", intentId: "pi-order-c-1-1" },
  exampleResponse: { intentId: "pi-order-c-1-1", leftToPay: { amount: 6000, currency: "USD" } },
});

/**
 * B1022 d.3 — MY GIFT CARDS in the magic-link account. Two views of the same
 * book, both read through the caller's own session: BOUGHT (the cards an
 * order the caller owns sold) and RECEIVED (cards addressed to the caller's
 * email that have gone out). A card's number and PIN are never in either —
 * they are digests in the store and cross the wire once, to the recipient's
 * email (B1023). The sender is named, never addressed; the order is never
 * named.
 */
const zBoughtCard = z
  .object({
    giftCardId: zId,
    lastFour: z.string().regex(/^\d{4}$/),
    status: zStatus,
    loaded: zMoney,
    balance: zNonNegMoney,
    issuedAt: zEpochMs,
    sendAt: zEpochMs.optional(),
    recipient: zParty.optional(),
    message: z.string().max(200).optional(),
    designId: zId.optional(),
  })
  .strict();

const zReceivedCard = z
  .object({
    giftCardId: zId,
    lastFour: z.string().regex(/^\d{4}$/),
    status: zStatus,
    loaded: zMoney,
    balance: zNonNegMoney,
    issuedAt: zEpochMs,
    from: z.string().max(120).optional(),
    message: z.string().max(200).optional(),
    designId: zId.optional(),
  })
  .strict();

const EXAMPLE_BOUGHT = {
  giftCardId: "gc_7f3a",
  lastFour: "8903",
  status: "scheduled",
  loaded: { amount: 5000, currency: "USD" },
  balance: { amount: 5000, currency: "USD" },
  issuedAt: 1_780_000_000_000,
  sendAt: 1_780_600_000_000,
  recipient: { name: "Sam", email: "sam@example.com" },
  message: "Happy day",
} as const;

export const giftcardMine = readOp({
  name: "giftcard.mine",
  owner: "customer",
  permission: "order:read",
  principals: ["CUSTOMER"],
  fields: {},
  response: z.object({ bought: z.array(zBoughtCard).max(500), received: z.array(zReceivedCard).max(500) }).strict(),
  exampleRequest: {},
  exampleResponse: {
    bought: [EXAMPLE_BOUGHT],
    received: [{ giftCardId: "gc_91c2", lastFour: "1177", status: "active", loaded: { amount: 2500, currency: "USD" }, balance: { amount: 2500, currency: "USD" }, issuedAt: 1_780_000_000_000, from: "Ada" }],
  },
});

/**
 * CHANGE THE DATE on a card the caller bought, before it sends. A card that
 * has gone out, a day that is not ahead, or one more than a year away is
 * refused in a sentence; a card that is not the caller's answers like an
 * absent one.
 */
export const giftcardReschedule = writeOp({
  name: "giftcard.reschedule",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER"],
  fields: { giftCardId: zId, sendAt: zEpochMs },
  response: zBoughtCard,
  exampleRequest: { giftCardId: "gc_7f3a", sendAt: 1_780_700_000_000 },
  exampleResponse: { ...EXAMPLE_BOUGHT, sendAt: 1_780_700_000_000 },
});
